// BriefBio.js

import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../utils/firebaseConfig';
import './Bulletin.css';

const ABBR = [
  'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'sr.', 'jr.',
  'inc.', 'ltd.', 'co.', 'vs.', 'etc.', 'e.g.', 'i.e.'
];

const isAbbreviation = (word) => {
  const cleaned = word.toLowerCase().replace(/[^a-z.]/g, '');
  return ABBR.includes(cleaned);
};

const splitText = (text = '') => {
  if (!text.trim()) return [];
  const words = text.trim().split(/\s+/);
  const sentences = [];
  let currentSentence = [];
  words.forEach((word, idx) => {
    currentSentence.push(word);
    const isLastWord = idx === words.length - 1;
    const endsWithPunctuation = /[.!?]$/.test(word);
    if ((endsWithPunctuation && !isAbbreviation(word)) || isLastWord) {
      sentences.push(currentSentence.join(' '));
      currentSentence = [];
    }
  });
  return sentences;
};

const BriefBio = () => {
  const [bios, setBios] = useState([]);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clickedNames, setClickedNames] = useState(() =>
    JSON.parse(localStorage.getItem('clickedBioNames') || '{}')
  );

  useEffect(() => {
    const biosRef = query(collection(db, 'briefBios'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(biosRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBios(data);
    });
    return () => unsubscribe();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bio?')) {
      try {
        await deleteDoc(doc(db, 'briefBios', id));
      } catch (error) {
        console.error('Error deleting bio:', error);
        alert('Failed to delete bio.');
      }
    }
  };

  const handleEdit = (bio) => {
    navigate(`/briefbioform?edit=${bio.id}&type=briefBio`);
  };

  const handleNameClick = (bioId) => {
    const updated = { ...clickedNames, [bioId]: true };
    setClickedNames(updated);
    localStorage.setItem('clickedBioNames', JSON.stringify(updated));
    navigate(`/briefbios/${bioId}`);
  };

  return (
    <div className="face-voting-container">
      {bios.map((bio) => (
        <div key={bio.id} className="face-question-box">
          <h2
            onClick={() => handleNameClick(bio.id)}
            style={{
              cursor: 'pointer',
              color: clickedNames[bio.id] ? '#551A8B' : '#1a0dab',
              textDecoration: 'none'
            }}
          >
            {bio.name}
          </h2>

          <div className="faces-row" style={{ justifyContent: 'center' }}>
            <div className="face-card">
              <div className="face-image-border-wrapper">
                <img
                  src={bio.imageUrl}
                  alt={bio.name}
                  className="voting-face-img"
                />
              </div>
            </div>
          </div>

          <div style={{
            marginTop: 20,
            fontSize: '1rem',
            lineHeight: 1.6,
            color: '#333',
            textAlign: 'left',
            maxWidth: '720px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {splitText(bio.bio).length > 1 ? (
              <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                {splitText(bio.bio).map((sentence, idx) => (
                  <li key={idx}>{sentence}</li>
                ))}
              </ul>
            ) : (
              <p>{bio.bio}</p>
            )}
          </div>

          {bio.sources?.length > 0 && (
            <div style={{ marginTop: 20, maxWidth: 720, marginInline: 'auto' }}>
              <h4 style={{ fontSize: 15, marginBottom: 6 }}>Sources:</h4>
              <ul style={{ paddingLeft: 18 }}>
                {bio.sources.map((src, idx) =>
                  src.name && src.link ? (
                    <li key={idx}>
                      <a
                        href={src.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1a0dab', textDecoration: 'underline' }}
                      >
                        {src.name}
                      </a>
                    </li>
                  ) : null
                )}
              </ul>
            </div>
          )}

          {isAdmin && (
            <div className="admin-controls">
              <button onClick={() => handleEdit(bio)} className="admin-edit-btn">
                Edit
              </button>
              <button onClick={() => handleDelete(bio.id)} className="admin-delete-btn">
                Delete
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default BriefBio;

